import React, {forwardRef} from 'react';
import {Chart as ChartJS, ChartComponentLike, ChartType, type DefaultDataPoint} from 'chart.js';
import {Chart, ChartProps} from 'react-chartjs-2';

export type ChartJSOrUndefined<TType extends ChartType = ChartType, TData = DefaultDataPoint<TType>, TLabel = unknown> = ChartJS<TType, TData, TLabel> | undefined;

/**
 * Creates a typed chart component for a given chart type
 * @param type The chart type (e.g., 'treemap')
 * @param registerables The Chart.js components to register
 * @returns A typed chart component
 */
export function createTypedChart<T extends ChartType, TData = DefaultDataPoint<T>, TLabel = unknown>(
    type: T,
    registerables: ChartComponentLike[]
) {
    // Register the chart components
    ChartJS.register(...registerables);
    if (type === 'pcp' || type === 'logarithmicPcp') {
        ChartJS.registry.addElements(registerables[1]); // The Axis, being LinearAxis or LogarithmicAxis
    }

    // Return a forwardRef component with proper typing
    return forwardRef<
        ChartJSOrUndefined<T, TData, TLabel>,
        Omit<ChartProps<T, TData, TLabel>, 'type'>
    >((props, ref) => {
        return React.createElement(Chart, {
            ...props,
            ref: ref,
            type: type,
        } as any);
    });
} 